/**
 * Удержание (G13 «Отказ»): кнопку надо держать `holdMs`; счётчик в моно.
 * При `escapeAtMs` кнопка «уезжает» (класс `escaped`, сдвиг без анимации)
 * и считается отпущенной: её надо нажать снова за `catchMs`, иначе
 * сорвалось. Время копится только пока держат. Отпустили раньше — исход
 * сразу с набранным временем. Клавиатура: пробел/Enter держат.
 */
import { t } from '@life/i18n';
import { useEffect, useRef, useState } from 'preact/hooks';

export const HOLD_TICK_MS = 100;

export interface HoldProps {
  label: string;
  holdMs: number;
  /** Момент (по набранному времени), когда кнопка уезжает; null — не уезжает. */
  escapeAtMs: number | null;
  catchMs: number;
  onDone: (result: { heldMs: number; caught: boolean }) => void;
}

export function Hold({ label, holdMs, escapeAtMs, catchMs, onDone }: HoldProps) {
  const [held, setHeld] = useState(0);
  const [phase, setPhase] = useState<'idle' | 'holding' | 'escaped'>('idle');
  const accumulated = useRef(0);
  const since = useRef<number | null>(null);
  const escaped = useRef(false);
  const caught = useRef(false);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const catchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const done = useRef(false);

  const stopTimers = () => {
    if (timer.current !== null) clearInterval(timer.current);
    timer.current = null;
    if (catchTimer.current !== null) clearTimeout(catchTimer.current);
    catchTimer.current = null;
  };

  useEffect(() => stopTimers, []);

  const total = () => accumulated.current + (since.current === null ? 0 : Date.now() - since.current);

  const finish = (ms: number) => {
    if (done.current) return;
    done.current = true;
    stopTimers();
    since.current = null;
    setPhase('idle');
    onDone({ heldMs: Math.min(holdMs, Math.round(ms)), caught: caught.current });
  };

  const pause = () => {
    accumulated.current = total();
    since.current = null;
    if (timer.current !== null) clearInterval(timer.current);
    timer.current = null;
  };

  const tick = () => {
    const ms = total();
    setHeld(ms);
    if (ms >= holdMs) {
      finish(ms);
      return;
    }
    if (escapeAtMs !== null && !escaped.current && ms >= escapeAtMs) {
      escaped.current = true;
      pause();
      setPhase('escaped');
      catchTimer.current = setTimeout(() => finish(accumulated.current), catchMs);
    }
  };

  const press = () => {
    if (done.current || since.current !== null) return;
    if (phase === 'escaped') {
      if (catchTimer.current !== null) clearTimeout(catchTimer.current);
      catchTimer.current = null;
      caught.current = true;
    }
    since.current = Date.now();
    setPhase('holding');
    timer.current = setInterval(tick, HOLD_TICK_MS);
  };

  const release = () => {
    if (done.current || since.current === null) return;
    finish(total());
  };

  const seconds = Math.max(0, Math.ceil((holdMs - held) / 1000));

  return (
    <div class="hold">
      <p class="hint mono" aria-live="polite">
        {phase === 'escaped' ? t('ui.minigame.hold.escaped', { s: catchMs / 1000 }) : t('ui.minigame.timer', { s: seconds })}
      </p>
      <button
        type="button"
        class={`btn accent hold-btn${phase === 'escaped' ? ' escaped' : ''}${phase === 'holding' ? ' holding' : ''}`}
        aria-pressed={phase === 'holding'}
        onPointerDown={(e) => {
          e.preventDefault();
          press();
        }}
        onPointerUp={release}
        onPointerCancel={release}
        onPointerLeave={() => {
          if (phase === 'holding') release();
        }}
        onKeyDown={(e) => {
          if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) {
            e.preventDefault();
            press();
          }
        }}
        onKeyUp={(e) => {
          if (e.key === ' ' || e.key === 'Enter') release();
        }}
      >
        {label}
      </button>
      <p class="hint">{t('ui.minigame.hold.hint', { s: holdMs / 1000 })}</p>
    </div>
  );
}
